// ZIP Coverage: territories, ZIP coverage rows, map, submissions history.
import { DEMO_CLIENT, MARKETS, MARKET_ZIPS, VERTICALS, addDays, iso, today } from '../client';
import { ZIP_FACTS } from '../zipFacts';
import { territoryId } from './20_budget';

// ZIPs our targeting filter screens out (low owner-occupancy etc.) — shows
// the prospect that we don't just blanket every ZIP they give us.
const FILTERED = new Set(['28202', '27601', '27401', '28204']);

function territories() {
  return MARKETS.map((m) => {
    const counts = Object.fromEntries(VERTICALS.map((v) => [String(v.id), MARKET_ZIPS[m.name].length]));
    return {
      id: territoryId(m),
      name: m.name,
      client: DEMO_CLIENT.id,
      client_name: DEMO_CLIENT.name,
      is_paused: false,
      paused_at: null,
      paused_state: null,
      paused_zip_count: 0,
      vertical_paused_counts: {},
      vertical_zip_counts: counts,
      vertical_zip_counts_all: counts,
      zip_count: MARKET_ZIPS[m.name].length,
    };
  });
}

function coverageRows() {
  const rows = [];
  let id = 1;
  MARKETS.forEach((m) => {
    MARKET_ZIPS[m.name].forEach((zip) => {
      const f = ZIP_FACTS[zip] || {};
      VERTICALS.forEach((v) => {
        rows.push({
          id: id++,
          zip,
          client_territory_id: territoryId(m),
          vertical_id: v.id,
          zip_type: f.type || 'STANDARD',
          population: f.population || 0,
          county: f.county || '',
          state: f.state || 'NC',
          city: f.city || '',
          latitude: f.lat,
          longitude: f.lng,
          client_name: DEMO_CLIENT.name,
        });
      });
    });
  });
  return rows;
}

// Rough hexagon around the centroid — stands in for the real ZIP outline.
function hexagon(lat, lng, r = 0.032) {
  const ring = [];
  for (let i = 0; i <= 6; i += 1) {
    const a = (Math.PI / 3) * (i % 6) + Math.PI / 6;
    ring.push([lng + (r * Math.cos(a)) / Math.cos((lat * Math.PI) / 180), lat + r * Math.sin(a)]);
  }
  return { type: 'Polygon', coordinates: [ring] };
}

function zipMap() {
  const zips = [];
  let id = 1;
  MARKETS.forEach((m) => {
    MARKET_ZIPS[m.name].forEach((zip) => {
      const f = ZIP_FACTS[zip];
      if (!f) return;
      zips.push({
        id: id++,
        zip_code: zip,
        territory_id: territoryId(m),
        territory_name: m.name,
        client_name: DEMO_CLIENT.name,
        is_filtered_out: FILTERED.has(zip),
        centroid: { lat: f.lat, lng: f.lng },
        geometry: hexagon(f.lat, f.lng),
      });
    });
  });
  return {
    zips,
    territories: MARKETS.map((m) => ({
      territory_id: territoryId(m),
      territory_name: m.name,
      client_name: DEMO_CLIENT.name,
      zip_count: MARKET_ZIPS[m.name].length,
      dissolved_active_geojson: null,
      dissolved_filtered_geojson: null,
    })),
    leads: [],
    meta: { geometry_mode: 'per_zip' },
  };
}

function submissions() {
  const t = today();
  const mk = (id, m, v, daysAgo, adds, removes, status = 'approved') => ({
    id,
    status,
    auto_tier: 'instant',
    auto_applies_at: null,
    client_territory: territoryId(m),
    vertical: v.id,
    territory_name: m.name,
    vertical_name: v.name,
    client_name: DEMO_CLIENT.name,
    submitted_at: `${iso(addDays(t, -daysAgo))}T14:10:00Z`,
    reviewed_at: `${iso(addDays(t, -daysAgo))}T14:25:00Z`,
    reviewed_by_email: 'team@leadgurus.example',
    submitted_by_email: DEMO_CLIENT.contact_email,
    reviewer_notes: '',
    add_count: adds.length,
    remove_count: removes.length,
    reverted: false,
    reverted_from: null,
    items: [
      ...adds.map((z, i) => ({ id: id * 100 + i, zip: z, zip_raw: z, action: 'add', latitude: ZIP_FACTS[z]?.lat, longitude: ZIP_FACTS[z]?.lng, city: ZIP_FACTS[z]?.city })),
      ...removes.map((z, i) => ({ id: id * 100 + 50 + i, zip: z, zip_raw: z, action: 'remove', latitude: ZIP_FACTS[z]?.lat, longitude: ZIP_FACTS[z]?.lng, city: ZIP_FACTS[z]?.city })),
    ],
  });
  const [clt, rdu, gso] = MARKETS;
  const [bath, windows, roofing] = VERTICALS;
  return [
    mk(31, clt, bath, 12, ['28025', '28027', '28115', '28117'], []),
    mk(30, rdu, windows, 34, ['27587', '27560'], ['27610']),
    mk(29, gso, roofing, 58, ['27262', '27265'], []),
    mk(28, clt, roofing, 81, ['28104', '28173', '28079'], []),
  ];
}

function filterSummary() {
  const rows = coverageRows();
  const verticals = VERTICALS.map((v) => {
    const vr = rows.filter((r) => r.vertical_id === v.id);
    const terrs = MARKETS.map((m) => {
      const tr = vr.filter((r) => r.client_territory_id === territoryId(m));
      const pop = tr.reduce((s, r) => s + r.population, 0);
      const filtered = tr.filter((r) => FILTERED.has(r.zip));
      const fPop = filtered.reduce((s, r) => s + r.population, 0);
      return {
        territory_id: territoryId(m),
        territory: m.name,
        fully_paused: false,
        zips: tr.length,
        zips_targeted: tr.length - filtered.length,
        zips_live: tr.length - filtered.length,
        zips_paused: 0,
        population: pop,
        population_targeted: pop - fPop,
        population_live: pop - fPop,
        reasons: {
          targeted: { zips: tr.length - filtered.length, population: pop - fPop },
          ownership: { zips: filtered.length, population: fPop },
        },
        criteria: ['Owner-occupied homes', 'Median home value', 'Household income'],
        reference: { dma: m.dma },
        misplaced: [],
      };
    });
    const sum = (k) => terrs.reduce((s, t) => s + t[k], 0);
    return {
      vertical_id: v.id,
      vertical: v.name,
      territories_paused: 0,
      zips_targeted: sum('zips_targeted'),
      zips_live: sum('zips_live'),
      zips_paused: 0,
      population: sum('population'),
      population_targeted: sum('population_targeted'),
      population_live: sum('population_live'),
      population_paused: 0,
      reasons: {
        targeted: { zips: sum('zips_targeted'), population: sum('population_targeted') },
        ownership: { zips: sum('zips') - sum('zips_targeted'), population: sum('population') - sum('population_targeted') },
      },
      territories: terrs,
    };
  });
  const v0 = verticals[0];
  return {
    reason_labels: { targeted: 'Targeted', ownership: 'Low home ownership' },
    totals: {
      population: v0.population,
      population_targeted: v0.population_targeted,
      zips: v0.territories.reduce((s, t) => s + t.zips, 0),
      zips_paused: 0,
      population_paused: 0,
      misplaced: 0,
    },
    verticals,
  };
}

const csv = () => ['zip,territory,vertical', ...coverageRows().map((r) => {
  const m = MARKETS.find((x) => territoryId(x) === r.client_territory_id);
  const v = VERTICALS.find((x) => x.id === r.vertical_id);
  return `${r.zip},${m.name},${v.name}`;
})].join('\n');

export const routes = [
  ['GET', 'dashboard/client-territories/pause-events', () => ({ events: [] })],
  ['GET', 'dashboard/client-territories/:id/pause-history', () => ({ events: [] })],
  ['GET', 'dashboard/client-territories', () => territories()],
  ['GET', 'dashboard/verticals/all-options', () => VERTICALS.map((v) => ({ id: v.id, vertical: v.name }))],
  ['GET', 'dashboard/client-zips/vertical_options', () => VERTICALS.map((v) => ({ id: v.id, vertical: v.name }))],
  ['GET', 'dashboard/client-zips/coverage_summary', () => coverageRows()],
  ['GET', 'dashboard/client-zips/filter-summary', () => filterSummary()],
  ['GET', 'dashboard/client-zips/export-csv', () => csv()],
  ['GET', 'dashboard/client-zip-map', () => zipMap()],
  ['GET', 'dashboard/zip-submissions/auto-review-config', () => ({ enabled: true, apply_delay_minutes: 15 })],
  ['GET', 'dashboard/zip-submissions/latest-by-territory', () => MARKETS.map((m) => ({
    territory_id: territoryId(m), reviewed_at: submissions()[0].reviewed_at, reviewed_by_email: 'team@leadgurus.example', submitted_by_email: DEMO_CLIENT.contact_email,
  }))],
  ['GET', 'dashboard/zip-submissions/launch-context', () => ({ budgets_by_territory: true, month_label: today().toLocaleString('en-US', { month: 'long' }) })],
  ['GET', 'dashboard/zip-submissions/geometry', ({ params }) => {
    const ids = (params.get('ids') || '').split(',').map(Number);
    const items = submissions().filter((s) => ids.includes(s.id)).flatMap((s) => s.items)
      .filter((i) => ZIP_FACTS[i.zip]).map((i) => ({ zip: i.zip, geometry: hexagon(ZIP_FACTS[i.zip].lat, ZIP_FACTS[i.zip].lng) }));
    return { items };
  }],
  ['GET', 'dashboard/zip-submissions/download-template', () => 'zip,territory,vertical,action\n'],
  ['GET', 'dashboard/zip-submissions', ({ params }) => {
    const status = params.get('status');
    const rows = submissions().filter((s) => !status || s.status === status);
    return { results: rows, count: rows.length, next: null, previous: null };
  }],
  // Read-only POST: lets the Manage ZIPs dialog validate what Will types.
  ['POST', 'dashboard/zip-submissions/check-overlap', ({ body }) => {
    const zips = body?.zip_codes || [];
    const details = {};
    zips.forEach((z) => {
      const f = ZIP_FACTS[z];
      details[z] = f ? { latitude: f.lat, longitude: f.lng, city: f.city, state: f.state } : { latitude: null, longitude: null, city: '', state: '' };
    });
    return { unknown_zips: [], conflicts: [], zip_details: details, has_conflicts: false };
  }, { silent: true }],
];
